import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { getCalApi } from "@calcom/embed-react";
import ProvinceSelect from "../components/ui/province-select";
import DateSelect from "../components/ui/date-select";

const AppointmentBookingPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    privacyAccepted: false,
    newsletterSubscribe: false,
    dateOfBirth: undefined,
    province: ''
  });
  
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [customMessage, setCustomMessage] = useState('');

  const provinces = [
    "Agrigento", "Alessandria", "Ancona", "Aosta", "Arezzo", "Ascoli Piceno", 
    "Asti", "Avellino", "Bari", "Barletta-Andria-Trani", "Belluno", "Benevento", 
    "Bergamo", "Biella", "Bologna", "Bolzano", "Brescia", "Brindisi", "Cagliari", 
    "Caltanissetta", "Campobasso", "Carbonia-Iglesias", "Caserta", "Catania", 
    "Catanzaro", "Chieti", "Como", "Cosenza", "Cremona", "Crotone", "Cuneo", 
    "Enna", "Fermo", "Ferrara", "Firenze", "Foggia", "Forlì-Cesena", "Frosinone", 
    "Genova", "Gorizia", "Grosseto", "Imperia", "Isernia", "La Spezia", "L'Aquila", 
    "Latina", "Lecce", "Lecco", "Livorno", "Lodi", "Lucca", "Macerata", "Mantova", 
    "Massa-Carrara", "Matera", "Messina", "Milano", "Modena", "Monza e Brianza", 
    "Napoli", "Novara", "Nuoro", "Ogliastra", "Olbia-Tempio", "Oristano", "Padova", 
    "Palermo", "Parma", "Pavia", "Perugia", "Pesaro e Urbino", "Pescara", "Piacenza", 
    "Pisa", "Pistoia", "Pordenone", "Potenza", "Prato", "Ragusa", "Ravenna", 
    "Reggio Calabria", "Reggio Emilia", "Rieti", "Rimini", "Roma", "Rovigo", 
    "Salerno", "Medio Campidano", "Sassari", "Savona", "Siena", "Siracusa", 
    "Sondrio", "Taranto", "Teramo", "Terni", "Torino", "Trapani", "Trento", 
    "Treviso", "Trieste", "Udine", "Varese", "Venezia", "Verbano-Cusio-Ossola", 
    "Vercelli", "Verona", "Vibo Valentia", "Vicenza", "Viterbo"
  ];

  const results = {
    success: {
      className: "text-green",
      title: "Richiesta Inviata!",
      icon: "✓",
      message: "Grazie per la tua richiesta! Ti contatterò il prima possibile per confermare l'appuntamento."
    },
    error: {
      className: "text-red-600",
      title: "Errore!",
      icon: "✕",
      message: "Si è verificato un errore durante l'invio della richiesta. Riprova più tardi."
    },
    warning: {
      className: "text-yellow-600",
      title: "Attenzione!",
      icon: "⚠",
      message: "Verifica i dati inseriti e riprova."
    },
    default: {
      className: "text-yellow-600",
      title: "Stato Sconosciuto",
      icon: "?",
      message: "Si è verificato uno stato imprevisto. Riprova più tardi."
    }
  };

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({"namespace":"visita-omnicomprensiva"});
      cal("ui", {
        "theme": "light",
        "styles": {
          "branding": {
            "brandColor": "#89ad4c"
          }
        },
        "hideEventTypeDetails": false,
        "layout": "month_view"
      });
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const submitAppointmentForm = async (formData) => {
    try {
      const response = await fetch('/api/contact/request-appointment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        return {
          status: 'error',
          message: 'Si è verificato un errore durante l\'elaborazione della tua richiesta. Per favore, riprova più tardi.'
        };
      }
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Additional validation for newsletter fields
    if (formData.newsletterSubscribe) {
      if (!formData.dateOfBirth || !formData.province) {
        setSubmissionStatus('warning');
        setCustomMessage('Per favore, compila tutti i campi richiesti per la newsletter.');
        return;
      }
    }
    
    try {
      const result = await submitAppointmentForm(formData);
      setSubmissionStatus(result.status);
      setCustomMessage(result.message);
    } catch (error) {
      setSubmissionStatus('error');
      setCustomMessage('Si è verificato un errore durante l\'invio del modulo. Per favore, riprova più tardi.');
    }
  };

  const slidingFormVariants = {
    hidden: { x: "100%" },
    visible: { 
      x: 0,
      transition: { type: "tween", duration: 0.4, ease: "easeInOut" }
    },
    exit: {
      x: "100%",
      transition: { type: "tween", duration: 0.4, ease: "easeInOut" }
    }
  };

  return (
    <div className="flex flex-col bg-beige">
      <section className="relative flex items-center bg-beige-light" style={{ minHeight: 'var(--content-height)' }}>
        {/* Background image for mobile */}
        <div 
          className="absolute inset-0 bg-cover bg-top bg-no-repeat md:hidden"
          style={{ 
            backgroundImage: 'url("/images/prenota/tree_on_the_hill.webp")',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-beige-light/75 to-beige-light"></div>
        </div>
        
        <div className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row md:items-center h-full">
          {/* Mobile Content */}
          <div className="md:hidden flex flex-col justify-end w-full pb-8" style={{ minHeight: 'var(--content-height)' }}>
            <div className="p-4 rounded-15 mt-auto">
              <h1 className="h1 text-green mb-4">
                Prenota la tua visita
              </h1>
              <p className="body text-text-dark mt-4 mb-6">
              Prenota la tua visita, lasciaci il tuo nomero di telefono e la Dottoressa Filzoli ti contatterá entro 48 ore.
              </p>
              <button 
                id="prenota-open-form-button"
                data-umami-event="prenota-open-form-button"
                onClick={() => setIsFormOpen(true)}
                className={`flex h4 items-center justify-center px-6 py-5 w-full font-bold rounded-full transition-colors duration-300 shadow-md ${
                  isFormOpen 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-beige-dark text-white hover:bg-accent'
                }`}
              >
                Prenota ora
              </button>
            </div>
          </div>

          {/* Desktop Left Content */}
          <div className="hidden md:flex md:w-1/2 md:pr-8 flex-col justify-center">
            <h1 className="h1 text-green">
              Prenota la tua visita
            </h1>
            <p className="body text-text-dark mt-4 mb-6">
            Prenota la tua visita, lasciaci il tuo nomero di telefono e la Dottoressa Filzoli ti contatterá entro 48 ore.
            </p>
            <button
              id="prenota-open-form-button"
              data-umami-event="prenota-open-form-button"
              onClick={() => setIsFormOpen(true)}
              className={`flex h4 items-center justify-center px-6 py-6 w-full md:w-auto font-bold rounded-15 transition-colors duration-300 shadow-md ${
                isFormOpen 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-beige-dark text-beige-light hover:bg-accent'
              }`}
            >
              Prenota ora
            </button>
          </div>
  
          {/* Desktop Right Content with Image */}
          <div className="hidden md:block md:w-1/2">
            <div className="w-full overflow-hidden relative">
              <img 
                src="/images/prenota/tree_on_the_hill.webp" 
                alt="Immagine di un pacifico albero su una collina"
                className="w-full rounded-30 shadow-lg object-cover object-center"
                style={{ height: 'calc(100vh - 200px)' }}
              />
              <AnimatePresence>
                {isFormOpen && (
                  <motion.div 
                    className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-30 overflow-y-auto hidden md:block"
                    variants={slidingFormVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                  >
                    {submissionStatus !== null ? (
                      <div className="flex flex-col items-center justify-center h-full space-y-6 text-center p-6">
                        <span className={`text-5xl ${results[submissionStatus]?.className || results.default.className}`}>
                          {results[submissionStatus]?.icon || results.default.icon}
                        </span>
                        <h2 className={`text-2xl font-medium ${results[submissionStatus]?.className || results.default.className}`}>
                          {results[submissionStatus]?.title || results.default.title}
                        </h2>
                        <p className="text-text-dark max-w-md">
                          {customMessage || results[submissionStatus]?.message || results.default.message}
                        </p>
                      </div>
                    ) : (
                      <div className="p-6 md:p-8">
                        <div className="flex justify-end">
                          <button 
                            onClick={() => setIsFormOpen(false)}
                            className="p-2 hover:bg-beige-light rounded-full transition-colors"
                          >
                            <X size={24} />
                          </button>
                        </div>
  
                        <form onSubmit={handleSubmit} className="space-y-6">
                          {/* Desktop form fields - same as mobile */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="name-desktop" className="block text-sm font-medium text-text-dark mb-2">
                                Nome *
                              </label>
                              <input
                                type="text"
                                id="name-desktop"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                              />
                            </div>
                            <div>
                              <label htmlFor="surname-desktop" className="block text-sm font-medium text-text-dark mb-2">
                                Cognome *
                              </label>
                              <input
                                type="text"
                                id="surname-desktop"
                                name="surname"
                                required
                                value={formData.surname}
                                onChange={handleChange}
                                className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                              />
                            </div>
                          </div>
  
                          <div>
                            <label htmlFor="email-desktop" className="block text-sm font-medium text-text-dark mb-2">
                              Email *
                            </label>
                            <input
                              type="email"
                              id="email-desktop"
                              name="email"
                              required
                              value={formData.email}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                            />
                          </div>
  
                          <div>
                            <label htmlFor="phone-desktop" className="block text-sm font-medium text-text-dark mb-2">
                              Numero di Telefono *
                            </label>
                            <input
                              type="tel"
                              id="phone-desktop"
                              name="phone"
                              required
                              value={formData.phone}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                            />
                          </div>
  
                          <div className="space-y-4">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                name="privacyAccepted"
                                required
                                checked={formData.privacyAccepted}
                                onChange={handleChange}
                                className="mr-2 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
                              />
                              <span className="text-sm text-text-dark">
                                Accetto la <a href="/privacy" className="text-accent hover:underline">politica sulla privacy</a> *
                              </span>
                            </label>
  
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                name="newsletterSubscribe"
                                checked={formData.newsletterSubscribe}
                                onChange={handleChange}
                                className="mr-2 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
                              />
                              <span className="text-sm text-text-dark">
                                Desidero iscrivermi alla newsletter
                              </span>
                            </label>
                          </div>
  
                          {formData.newsletterSubscribe && (
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-text-dark mb-2">
                                  Data di Nascita *
                                </label>
                                <DateSelect 
                                  value={formData.dateOfBirth}
                                  onChange={(date) => setFormData(prev => ({ ...prev, dateOfBirth: date }))}
                                  className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                                />
                              </div>
  
                              <div>
                                <label className="block text-sm font-medium text-text-dark mb-2">
                                  Provincia *
                                </label>
                                <ProvinceSelect 
                                  value={formData.province}
                                  onChange={(selectedProvince) => setFormData(prev => ({ ...prev, province: selectedProvince }))}
                                  provinces={provinces}
                                />
                              </div>
                            </div>
                          )}
  
                            <button
                            id="prenota-send-form-button"
                            data-umami-event="prenota-send-form-button"
                            type="submit"
                            className="w-full flex items-center justify-center px-6 py-4 bg-beige-dark text-white rounded-full hover:bg-accent transition-colors duration-300"
                          >
                            <Send className="mr-2" size={20} />
                            Prenota ora
                          </button>
                        </form>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
  
        {/* Form Overlay for both Mobile and Desktop */}
        <AnimatePresence>
          {isFormOpen && (
            <motion.div 
              className="fixed inset-0 z-50 bg-white/95 backdrop-blur-sm block md:hidden"
              variants={slidingFormVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {submissionStatus !== null ? (
                <div className="flex flex-col items-center justify-center h-full space-y-6 text-center p-6">
                  <span className={`text-5xl ${results[submissionStatus]?.className || results.default.className}`}>
                    {results[submissionStatus]?.icon || results.default.icon}
                  </span>
                  <h2 className={`text-2xl font-medium ${results[submissionStatus]?.className || results.default.className}`}>
                    {results[submissionStatus]?.title || results.default.title}
                  </h2>
                  <p className="text-text-dark max-w-md">
                    {customMessage || results[submissionStatus]?.message || results.default.message}
                  </p>
                  
                </div>
              ) : (
                <div className="h-full overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-end">
                      <button 
                        onClick={() => setIsFormOpen(false)}
                        className="p-2 hover:bg-beige-light rounded-full transition-colors"
                      >
                        <X size={24} />
                      </button>
                    </div>
  
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-text-dark mb-2">
                            Nome *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>
                        <div>
                          <label htmlFor="surname" className="block text-sm font-medium text-text-dark mb-2">
                            Cognome *
                          </label>
                          <input
                            type="text"
                            id="surname"
                            name="surname"
                            required
                            value={formData.surname}
                            onChange={handleChange}
                            className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>
                      </div>
  
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-text-dark mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                        />
                      </div>
  
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-text-dark mb-2">
                          Numero di Telefono *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                        />
                      </div>
  
                      <div className="space-y-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="privacyAccepted"
                            required
                            checked={formData.privacyAccepted}
                            onChange={handleChange}
                            className="mr-2 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
                          />
                          <span className="text-sm text-text-dark">
                            Accetto la <a href="/privacy" className="text-accent hover:underline">politica sulla privacy</a> *
                          </span>
                        </label>
  
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="newsletterSubscribe"
                            checked={formData.newsletterSubscribe}
                            onChange={handleChange}
                            className="mr-2 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent"
                          />
                          <span className="text-sm text-text-dark">
                            Desidero iscrivermi alla newsletter
                          </span>
                        </label>
                      </div>
  
                      {formData.newsletterSubscribe && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-text-dark mb-2">
                              Data di Nascita *
                            </label>
                            <DateSelect 
                              value={formData.dateOfBirth}
                              onChange={(date) => setFormData(prev => ({ ...prev, dateOfBirth: date }))}
                              className="w-full px-4 py-3 rounded-full border border-beige-dark/20 focus:outline-none focus:ring-2 focus:ring-accent"
                            />
                          </div>
  
                          <div>
                            <label className="block text-sm font-medium text-text-dark mb-2">
                              Provincia *
                            </label>
                            <ProvinceSelect 
                              value={formData.province}
                              onChange={(selectedProvince) => setFormData(prev => ({ ...prev, province: selectedProvince }))}
                              provinces={provinces}
                            />
                          </div>
                        </div>
                      )}
  
                        <button
                        id="prenota-send-form-button"
                        data-umami-event="prenota-send-form-button"
                        type="submit"
                        className="w-full flex items-center justify-center px-6 py-4 bg-beige-dark text-white rounded-full hover:bg-accent transition-colors duration-300"
                        >
                        <Send className="mr-2" size={20} />
                        Prenota ora
                      </button>
                    </form>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
};

export default AppointmentBookingPage;